/**
 * MyCopro - Poll Store (Zustand + Firestore)
 * + Notification automatique lors de la création d'un sondage
 * + Notification automatique lors de la clôture d'un sondage
 * src/stores/pollStore.ts
 */

import { create } from 'zustand';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { logger } from '@/utils/logger';

// Types
export interface PollOption {
  id: string;
  text: string;
  votes: number;
  voterIds: string[];
}

export interface Poll {
  id: string;
  question: string;
  options: PollOption[];
  isActive: boolean;
  /** Alias for isActive — kept for backward compatibility with pages that use poll.active */
  active?: boolean;
  restrictedTo: 'all' | 'owners' | 'council';
  createdAt: string;
  createdBy: string;
  createdByName: string;
  closedAt?: string;
  closedBy?: string;
  closedByName?: string;
  residenceId: string;
}

interface PollState {
  polls: Poll[];
  isLoading: boolean;
  error: string | null;
  unsubscribe: Unsubscribe | null;
  /** Placeholder kept for type-compatibility with pages that destructure userVotes from the store */
  userVotes: Record<string, Record<string, number>>;

  // Actions
  subscribeToPolls: (residenceId: string) => void;
  unsubscribeFromPolls: () => void;
  createPoll: (residenceId: string, data: {
    question: string;
    options: string[];
    restrictedTo: 'all' | 'owners' | 'council';
    createdBy: string;
    createdByName: string;
  }) => Promise<string | null>;
  vote: (residenceId: string, pollId: string, optionId: string, userId: string) => Promise<boolean>;
  closePoll: (residenceId: string, pollId: string, closedBy: string, closedByName: string) => Promise<boolean>;
  reopenPoll: (residenceId: string, pollId: string) => Promise<boolean>;
  deletePoll: (residenceId: string, pollId: string) => Promise<boolean>;
  hasUserVoted: (pollId: string, userId: string) => boolean;
  getUserVotedOption: (pollId: string, userId: string) => string | null;
  clearPolls: () => void;
}

// ========================================
// HELPER: Envoyer une notification (création)
// ========================================

const sendPollCreatedNotification = async (
  residenceId: string,
  question: string,
  restrictedTo: 'all' | 'owners' | 'council',
  createdBy: string,
  createdByName: string,
  pollId: string
) => {
  try {
    const notificationsRef = collection(db, `residences/${residenceId}/notifications`);
    
    // Convertir restrictedTo en format attendu par useNotifications
    let restrictedToArray: string[] = [];
    if (restrictedTo === 'owners') {
      restrictedToArray = ['owner'];
    } else if (restrictedTo === 'council') {
      restrictedToArray = ['council'];
    }
    // Si 'all', restrictedToArray reste vide = tous les utilisateurs

    // Tronquer la question si trop longue
    const shortQuestion = question.length > 50 
      ? question.substring(0, 47) + '...' 
      : question;

    const notificationData: Record<string, any> = {
      title: '📊 Nouveau sondage',
      message: `"${shortQuestion}" - Donnez votre avis !`,
      type: 'info',
      createdAt: Timestamp.now(),
      sentBy: createdBy,
      sentByName: createdByName,
      readBy: [],
      link: `/polls`
    };

    // Ajouter restrictedTo seulement si non vide
    if (restrictedToArray.length > 0) {
      notificationData.restrictedTo = restrictedToArray;
    }

    await addDoc(notificationsRef, notificationData);
    logger.db(' Notification sent for new poll:', shortQuestion);
  } catch (error) {
    console.error('[pollStore] Error sending creation notification:', error);
  }
};

// ========================================
// HELPER: Envoyer une notification (clôture)
// ========================================

const sendPollClosedNotification = async (
  residenceId: string,
  poll: Poll,
  closedBy: string,
  closedByName: string
) => {
  try {
    const notificationsRef = collection(db, `residences/${residenceId}/notifications`);
    
    // Convertir restrictedTo en format attendu
    let restrictedToArray: string[] = [];
    if (poll.restrictedTo === 'owners') {
      restrictedToArray = ['owner'];
    } else if (poll.restrictedTo === 'council') {
      restrictedToArray = ['council'];
    }

    // Tronquer la question si trop longue
    const shortQuestion = poll.question.length > 40 
      ? poll.question.substring(0, 37) + '...' 
      : poll.question;

    // Calculer le résultat (option gagnante)
    let resultMessage = '';
    if (poll.options && poll.options.length > 0) {
      const totalVotes = poll.options.reduce((sum, opt) => sum + (opt.votes || 0), 0);
      
      if (totalVotes > 0) {
        // Trouver l'option avec le plus de votes
        const winningOption = poll.options.reduce((max, opt) => 
          (opt.votes || 0) > (max.votes || 0) ? opt : max
        , poll.options[0]);
        
        const percentage = Math.round((winningOption.votes / totalVotes) * 100);
        resultMessage = ` Résultat : "${winningOption.text}" (${percentage}%)`;
      } else {
        resultMessage = ' Aucun vote enregistré.';
      }
    }

    const notificationData: Record<string, any> = {
      title: '📊 Sondage terminé',
      message: `"${shortQuestion}" est clôturé.${resultMessage}`,
      type: 'success',
      createdAt: Timestamp.now(),
      sentBy: closedBy,
      sentByName: closedByName,
      readBy: [],
      link: `/polls`
    };

    // Ajouter restrictedTo seulement si non vide
    if (restrictedToArray.length > 0) {
      notificationData.restrictedTo = restrictedToArray;
    }

    await addDoc(notificationsRef, notificationData);
    logger.db(' Notification sent for closed poll:', shortQuestion);
  } catch (error) {
    console.error('[pollStore] Error sending close notification:', error);
  }
};

// ========================================
// STORE
// ========================================

export const usePollStore = create<PollState>((set, get) => ({
  polls: [],
  isLoading: false,
  error: null,
  unsubscribe: null,
  userVotes: {},

  // S'abonner aux sondages en temps réel
  subscribeToPolls: (residenceId: string) => {
    if (!residenceId) return;

    // Se désabonner d'abord si déjà abonné
    const { unsubscribe: currentUnsubscribe } = get();
    if (currentUnsubscribe) {
      currentUnsubscribe();
    }

    set({ isLoading: true });

    const pollsRef = collection(db, 'residences', residenceId, 'polls');
    const q = query(pollsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const polls: Poll[] = snapshot.docs.map(doc => {
          const data = doc.data();
          const isActive = data.isActive ?? true;
          return {
            id: doc.id,
            question: data.question || '',
            options: data.options || [],
            isActive,
            active: isActive, // alias for backward-compat
            restrictedTo: data.restrictedTo || 'all',
            createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            createdBy: data.createdBy || '',
            createdByName: data.createdByName || '',
            closedAt: data.closedAt?.toDate?.()?.toISOString() || undefined,
            closedBy: data.closedBy || undefined,
            closedByName: data.closedByName || undefined,
            residenceId: residenceId
          };
        });

        set({ polls, isLoading: false, error: null });
      },
      (error) => {
        console.error('Error subscribing to polls:', error);
        set({ error: 'Erreur de connexion aux sondages', isLoading: false });
      }
    );

    set({ unsubscribe });
  },

  // Se désabonner
  unsubscribeFromPolls: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
      set({ unsubscribe: null });
    }
  },

  // Créer un nouveau sondage
  createPoll: async (residenceId, data) => {
    if (!residenceId) return null;

    try {
      const pollsRef = collection(db, 'residences', residenceId, 'polls');
      
      // Créer les options avec IDs uniques
      const options: PollOption[] = data.options
        .filter(opt => opt.trim())
        .map((text, index) => ({
          id: `opt_${Date.now()}_${index}`,
          text: text.trim(),
          votes: 0,
          voterIds: []
        }));

      const docData = {
        question: data.question.trim(),
        options,
        isActive: true,
        restrictedTo: data.restrictedTo,
        createdAt: Timestamp.now(),
        createdBy: data.createdBy,
        createdByName: data.createdByName
      };

      const docRef = await addDoc(pollsRef, docData);
      logger.db('Poll created with ID:', docRef.id);

      // ✅ Envoyer une notification à l'audience concernée
      await sendPollCreatedNotification(
        residenceId,
        data.question,
        data.restrictedTo,
        data.createdBy,
        data.createdByName,
        docRef.id
      );

      return docRef.id;
    } catch (error) {
      console.error('Error creating poll:', error);
      return null;
    }
  },

  // Voter pour une option
  vote: async (residenceId, pollId, optionId, userId) => {
    if (!residenceId || !pollId || !optionId || !userId) return false;

    try {
      const { polls } = get();
      const poll = polls.find(p => p.id === pollId);
      if (!poll || !poll.isActive) return false;

      // Vérifier si l'utilisateur a déjà voté
      const hasVoted = poll.options.some(opt => opt.voterIds?.includes(userId));
      if (hasVoted) {
        logger.db('User has already voted');
        return false;
      }

      // Mettre à jour les options
      const updatedOptions = poll.options.map(opt => {
        if (opt.id === optionId) {
          return {
            ...opt,
            votes: (opt.votes || 0) + 1,
            voterIds: [...(opt.voterIds || []), userId]
          };
        }
        return opt;
      });

      const pollRef = doc(db, 'residences', residenceId, 'polls', pollId);
      await updateDoc(pollRef, { options: updatedOptions });
      
      logger.db('Vote registered');
      return true;
    } catch (error) {
      console.error('Error voting:', error);
      return false;
    }
  },

  // Clôturer un sondage
  closePoll: async (residenceId, pollId, closedBy, closedByName) => {
    if (!residenceId || !pollId) return false;

    try {
      // Récupérer le sondage pour la notification
      const { polls } = get();
      const poll = polls.find(p => p.id === pollId);

      const pollRef = doc(db, 'residences', residenceId, 'polls', pollId);
      await updateDoc(pollRef, { 
        isActive: false,
        closedAt: Timestamp.now(),
        closedBy: closedBy,
        closedByName: closedByName
      });

      // ✅ Envoyer une notification avec les résultats
      if (poll) {
        await sendPollClosedNotification(residenceId, poll, closedBy, closedByName);
      }

      return true;
    } catch (error) {
      console.error('Error closing poll:', error);
      return false;
    }
  },

  // Rouvrir un sondage
  reopenPoll: async (residenceId, pollId) => {
    if (!residenceId || !pollId) return false;

    try {
      const pollRef = doc(db, 'residences', residenceId, 'polls', pollId);
      await updateDoc(pollRef, { 
        isActive: true,
        closedAt: null,
        closedBy: null,
        closedByName: null
      });
      return true;
    } catch (error) {
      console.error('Error reopening poll:', error);
      return false;
    }
  },

  // Supprimer un sondage
  deletePoll: async (residenceId, pollId) => {
    if (!residenceId || !pollId) return false;

    try {
      const pollRef = doc(db, 'residences', residenceId, 'polls', pollId);
      await deleteDoc(pollRef);
      return true;
    } catch (error) {
      console.error('Error deleting poll:', error);
      return false;
    }
  },

  // Vérifier si un utilisateur a voté
  hasUserVoted: (pollId, userId) => {
    const { polls } = get();
    const poll = polls.find(p => p.id === pollId);
    if (!poll) return false;
    return poll.options.some(opt => opt.voterIds?.includes(userId));
  },

  // Obtenir l'option votée par un utilisateur
  getUserVotedOption: (pollId, userId) => {
    const { polls } = get();
    const poll = polls.find(p => p.id === pollId);
    if (!poll) return null;
    const votedOption = poll.options.find(opt => opt.voterIds?.includes(userId));
    return votedOption?.id || null;
  },

  // Vider les sondages
  clearPolls: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
    }
    set({ polls: [], unsubscribe: null });
  }
}));

export default usePollStore;
